import { BASE_URL, GET, POST } from './httpClient';
import SessionService from './sessionService';

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);

const getResultPayload = (response) => response?.data?.results ?? response?.data ?? {};

const normalizeInterval = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized.includes('year')) return 'year';
    if (normalized.includes('month')) return 'month';
    return normalized;
};

export const normalizeTier = (value) => {
    const normalized = String(value || '').trim().toUpperCase();
    return normalized || 'FREE';
};

const inferTierFromLookupKey = (priceLookupKey) => {
    const lookup = String(priceLookupKey || '').toLowerCase();
    if (lookup.includes('pro')) {
        return 'PRO';
    }
    return 'FREE';
};

const normalizePrice = (price) => {
    const lookupKey = price?.priceLookupKey || price?.lookupKey || price?.lookup_key || '';
    const recurring = price?.recurring || {};
    const interval = normalizeInterval(
        price?.interval ||
        recurring.interval ||
        price?.billingInterval
    );

    return {
        id: price?.id || lookupKey,
        name: price?.name || price?.nickname || `Pro ${interval === 'year' ? 'Yearly' : 'Monthly'}`,
        description: price?.description || '',
        currency: String(price?.currency || 'usd').toUpperCase(),
        unitAmount: Number(price?.unitAmount ?? price?.unit_amount ?? 0),
        interval,
        intervalCount: Number(price?.intervalCount ?? recurring.interval_count ?? 1),
        available: price?.available !== false,
        priceLookupKey: lookupKey,
        metadata: price?.metadata || {}
    };
};

const normalizePricesPayload = (payload) => {
    const rawPrices = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.prices)
            ? payload.prices
            : Array.isArray(payload?.items)
                ? payload.items
                : [
                    payload?.monthly,
                    payload?.yearly
                ].filter(Boolean);

    return rawPrices
        .map(normalizePrice)
        .filter((price) => {
            const lookup = String(price.priceLookupKey || '').toLowerCase();
            return price.priceLookupKey && lookup.includes('pro') && (price.interval === 'month' || price.interval === 'year');
        })
        .sort((a, b) => {
            const order = { month: 0, year: 1 };
            return (order[a.interval] ?? 99) - (order[b.interval] ?? 99);
        });
};

const normalizeSubscriptionPayload = (payload) => {
    const subscription = payload?.subscription || payload;
    const priceLookupKey = subscription?.priceLookupKey ||
        subscription?.price_lookup_key ||
        subscription?.price?.lookup_key ||
        subscription?.price?.lookupKey ||
        '';
    const status = String(subscription?.status || payload?.status || 'inactive').toLowerCase();
    const currentTier = normalizeTier(
        payload?.currentTier ||
        subscription?.currentTier ||
        subscription?.tier ||
        inferTierFromLookupKey(priceLookupKey)
    );

    return {
        currentTier,
        subscription: {
            status,
            priceLookupKey,
            currentPeriodEnd: subscription?.currentPeriodEnd ||
                subscription?.current_period_end ||
                null
        },
        isPaid: currentTier === 'PRO' && ACTIVE_SUBSCRIPTION_STATUSES.has(status)
    };
};

export const formatPriceDisplay = (unitAmount, currency) => {
    const amount = Number(unitAmount || 0) / 100;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase()
    }).format(amount);
};

export const fetchPrices = async () => {
    const response = await GET(`${BASE_URL}/payments/prices`);
    return normalizePricesPayload(getResultPayload(response));
};

export const createCheckoutSession = async ({ priceLookupKey, successUrl, cancelUrl }) => {
    const response = await POST(`${BASE_URL}/payments/checkout-session`, {
        priceLookupKey,
        successUrl,
        cancelUrl
    });

    const payload = getResultPayload(response);

    return {
        checkoutUrl: payload?.checkoutUrl || payload?.url || response?.data?.checkoutUrl,
        sessionId: payload?.sessionId || payload?.id || null
    };
};

export const createPortalSession = async () => {
    const response = await POST(`${BASE_URL}/payments/portal-session`, {});
    const payload = getResultPayload(response);

    return {
        url: payload?.url || response?.data?.url || null
    };
};

export const fetchSubscriptionState = async () => {
    const response = await GET(`${BASE_URL}/payments/subscription`);
    return normalizeSubscriptionPayload(getResultPayload(response));
};

export const refreshSubscriptionState = async () => {
    const sessionService = SessionService.getInstance();
    const subscriptionState = await fetchSubscriptionState();
    sessionService.setPaymentSubscription(subscriptionState);
    return subscriptionState;
};

export const getCachedSubscriptionState = () => {
    return SessionService.getInstance().getPaymentSubscription();
};

export const hasActiveProSubscription = (subscriptionState = getCachedSubscriptionState()) => {
    return !!subscriptionState?.isPaid;
};
